import { UNRESOLVED_PLAY_NAME, type EnrollmentItem, type QueueResponse } from '@shared/flow/enrollment';

/**
 * Pure derivations over the queue's `groups` payload that the rail's Plays
 * pane shares (play-name resolution and the By Contact re-projection). They
 * lived at the top of the V1 `QueueBody.tsx` until that body was deleted with
 * the V1 dock.
 */

/**
 * The ONE play-name resolution every queue surface shares (D2), now read off
 * the GROUP rather than scanned out of `plays` (U4B).
 *
 * `plays` is the trimmed live catalog: it no longer carries per-instance rows
 * for a `catalog: true` exposure kind (today: `gong_flow`, ~660 rows trimmed),
 * so the old `plays.find` scan silently came up empty for exactly the groups
 * that most need a name. `group.play_name` already carries the server's own
 * `UNRESOLVED_PLAY_NAME` fallback (`resolveGroupPlayInfo`,
 * `@shared/flow/enrollment`) for an instance that has aged out of the live
 * catalog — the `??` here is pure type-narrowing (the schema keeps the field
 * nullable; the producer always sets it for a real assigned group), not a
 * second fallback path.
 *
 * D7 (flow-contacts-bulk-send design-fidelity plan, item 7) — this `??`
 * fallback used to be `` `Play ${group.play_instance_ref}` ``, which put a
 * raw Salesforce campaign id in front of a rep as visible copy whenever a
 * group somehow reached this component with a null `play_name`. Changed to
 * the play-type-agnostic `UNRESOLVED_PLAY_NAME` (`@shared/flow/enrollment` —
 * the SAME constant the producer, `resolveGroupPlayInfo` in
 * `src/services/flow/queue.ts`, uses for its own fallback, and that
 * `EnrollQueueContext.tsx`'s send-toast snapshot also imports) — a raw
 * `701…` id must never be visible copy, tooltip or no.
 *
 * Shared by the rail's `PlaysPane`, `ByPlay`, `ByContact` and `PlaySection`.
 */
export function resolveGroupName(group: { play_name: string | null; play_instance_ref: string | null }): string {
  return group.play_name ?? UNRESOLVED_PLAY_NAME;
}

/** U5 — the Queue List's two groupings; view-local, not persisted. */
export type QueueGrouping = 'by-play' | 'by-contact';

/** One `ContactQueueRow` mount's worth of data. */
export interface ContactQueueEntry {
  item: EnrollmentItem;
  playName: string;
  /**
   * U4B/U5B — group-sourced capability data. By Contact flattens groups away,
   * but these are properties of the row's PLAY, and the trimmed live catalog
   * (`plays`) can no longer answer for a `catalog: true` exposure kind. So
   * they are resolved off the group here, while it is still in hand, exactly
   * as `PlayQueueCard` resolves them for By Play.
   */
  hasEditableCopy: boolean;
  /**
   * Group-sourced, same "resolve off the group, not `plays.find`" posture
   * as `hasEditableCopy` above; see `PlayOption.copy_required`'s doc
   * comment.
   */
  copyRequired: boolean;
  gongFlowId: string | null;
  bodyFormat: 'plain' | 'rich';
}

/**
 * U5 (R9/R12/R7) — By Contact is a client-side re-projection of the SAME
 * `groups` data the By Play sections render, flattened to one entry per contact.
 * By the DB-enforced invariant ("at most one open queue item per contact,
 * system-wide" — see the plan's Problem Frame) a contact can appear in at
 * most one assigned group's items array, so this is a straight flatten, not a
 * merge; a duplicate contact id across groups would mean the invariant was
 * violated server-side, which this asserts (via `console.error`) and
 * recovers from by keeping only the first occurrence and dropping the rest,
 * rather than rendering a second row for the same contact.
 */
export function deriveContactQueueEntries(assignedGroups: QueueResponse['groups']): ContactQueueEntry[] {
  const seen = new Set<string>();
  const entries: ContactQueueEntry[] = [];
  for (const group of assignedGroups) {
    for (const item of group.items) {
      if (seen.has(item.contact_id)) {
        // eslint-disable-next-line no-console -- invariant-violation guard (U5 Approach), not routine logging.
        console.error(
          `Flow queue invariant violation: contact ${item.contact_id} has more than one open item across plays; rendering only the first in By Contact.`,
        );
        continue;
      }
      seen.add(item.contact_id);
      entries.push({
        item,
        playName: resolveGroupName(group),
        // Fail CLOSED on missing capability data (Reflection fix B2).
        hasEditableCopy: group.has_editable_copy ?? false,
        copyRequired: group.copy_required ?? false,
        gongFlowId: group.ui?.has_step_overrides ? group.play_instance_ref : null,
        bodyFormat: group.ui?.body_format ?? 'plain',
      });
    }
  }
  // D25 (flow-contacts-bulk-send QA round 3, item 13) — newest-added-first,
  // globally across plays (not grouped-by-play then created-at ASC within
  // each group, which is what the flatten above produces on its own).
  // `created_at` is already an ISO string on the wire (EnrollmentItemSchema,
  // @shared/flow/enrollment), so `localeCompare` DESC is a correct
  // lexicographic date sort with no `Date` allocation per comparison — no
  // backend change needed. The `id` DESC tiebreak matters: a bulk add writes
  // many rows in one transaction and they can share a `created_at` down to
  // the millisecond, which without a tiebreak would give a non-deterministic
  // order that shuffles between renders; item ids are monotonic, so id DESC
  // is the correct within-timestamp "newest first". Runs AFTER the
  // duplicate-contact guard above — "first occurrence" still means first in
  // server order, unaffected by this sort.
  entries.sort((a, b) => b.item.created_at.localeCompare(a.item.created_at) || b.item.id - a.item.id);
  return entries;
}
